import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { ActiveSessionBanner } from '@/shared/components/ActiveSessionBanner';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { AppSidebar } from '@/shared/components/AppSidebar';
import { OfflineIndicator } from '@/shared/components/OfflineIndicator';
import { useClientLock } from '@/features/auth/contexts/ClientLockContext';
import { ClientLockToggle } from '@/features/auth/components/ClientLockToggle';
import { AdminPlanSwitcher } from '@/features/auth/components/AdminPlanSwitcher';
import { GlobalSearch } from '@/shared/components/GlobalSearch';
import { NotificationBell } from '@/shared/components/NotificationBell';
import { useSubscription } from '@/features/subscription/hooks/useSubscription';
import { Menu } from 'lucide-react';
import { SuspendedScreen } from '@/features/studio/components/SuspendedScreen';

export function AppLayout({ children }: { children: React.ReactNode }) {
  const { isLocked, requirePin } = useClientLock();
  const location = useLocation();
  const navigate = useNavigate();
  const { isInTrial, trialDaysLeft } = useSubscription();

  const isOnContractForm = location.pathname.startsWith('/contract/');

  // When navigating away from contract form while locked, prompt PIN to unlock
  useEffect(() => {
    if (isLocked && !isOnContractForm) {
      requirePin(() => {}, { releaseLock: true });
    }
  }, [location.pathname]);

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        {/* The sidebar is hidden in client mode to remove free navigation. */}
        {!isLocked && <AppSidebar />}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top bar — paddingTop pushes content below iOS status bar on PWA */}
          <header
            className="sticky top-0 z-30 border-b border-border/50 backdrop-blur-md bg-background/80 flex flex-col"
            style={{ paddingTop: 'env(safe-area-inset-top)' }}
          >
          <OfflineIndicator />
          <div className="h-14 flex items-center justify-between px-4 gap-3">
            {isLocked ? (
              <div className="w-9" aria-hidden />
            ) : (
              <SidebarTrigger className="text-muted-foreground hover:text-foreground shrink-0">
                <Menu size={20} />
              </SidebarTrigger>
            )}

            {!isLocked && <GlobalSearch />}

            <div className="flex items-center gap-1.5 shrink-0">
              <AdminPlanSwitcher />
              {isOnContractForm && <ClientLockToggle />}
              {!isLocked && <NotificationBell />}
              {isInTrial && (
                <button
                  onClick={() => navigate('/pricing')}
                  className={`hidden sm:flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                    trialDaysLeft <= 3
                      ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400 hover:bg-amber-500/25'
                      : 'bg-blue-500/15 text-blue-600 dark:text-blue-400 hover:bg-blue-500/25'
                  }`}
                >
                  Essai — {trialDaysLeft}j
                </button>
              )}
            </div>
          </div>
          </header>
          <ActiveSessionBanner />
          <main className="flex-1 overflow-y-auto">
            {children}
          </main>
        </div>
      </div>
      <SuspendedScreen />
    </SidebarProvider>
  );
}
